import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Gift, Heart, Sparkles } from 'lucide-react';
import Questionnaire from './Questionnaire';
import AutobiographyViewer from './AutobiographyViewer';

const GiftRedeem = () => {
  const { giftCode } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('welcome'); // welcome, questionnaire, viewer
  const [autobiographyData, setAutobiographyData] = useState(null);

  useEffect(() => {
    fetchGift();
  }, [giftCode]);

  const fetchGift = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift/redeem/${giftCode}`);
      if (res.ok) {
        const data = await res.json();
        setGift(data);
        if (data.autobiography_id) {
          // Already redeemed, fetch the autobiography
          const autoRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/autobiography/${data.autobiography_id}`);
          if (autoRes.ok) {
            setAutobiographyData(await autoRes.json());
            setView('viewer');
          }
        }
      }
    } catch (err) {
      console.error('Gift fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaireComplete = async (resp) => {
    setAutobiographyData(resp);
    // Mark gift as redeemed
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift/complete/${giftCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autobiography_id: resp.id }),
      });
    } catch (err) { console.error('Gift complete error:', err); }
    setView('viewer');
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
    </div>
  );

  if (!gift) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center px-4">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Gift Not Found</h2>
        <p className="text-gray-600 mb-6">This gift code may be invalid or expired.</p>
        <Button onClick={() => navigate('/')} className="btn-primary text-white rounded-full px-8 py-5 border-0">Go to Home</Button>
      </div>
    </div>
  );

  if (view === 'questionnaire') {
    return <Questionnaire onComplete={handleQuestionnaireComplete} userData={{ name: gift.recipient_name, email: gift.recipient_email }} />;
  }

  if (view === 'viewer' && autobiographyData) {
    return <AutobiographyViewer autobiographyData={autobiographyData} onBack={() => navigate('/')} />;
  }

  // Welcome screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-amber-50 flex items-center justify-center px-4 py-12" data-testid="gift-redeem-page">
      <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 max-w-lg w-full text-center fade-in">
        <div className="flex justify-center gap-3 mb-6">
          <Gift className="w-8 h-8 text-pink-400 float-animation" />
          <Heart className="w-8 h-8 text-purple-400 float-animation" style={{ animationDelay: '0.5s' }} />
          <Sparkles className="w-8 h-8 text-amber-400 float-animation" style={{ animationDelay: '1s' }} />
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-testid="gift-welcome-title">
          You've Received a Gift!
        </h2>

        <p className="text-gray-600 mb-4">
          <strong>{gift.buyer_name}</strong> has given you a special gift — your very own AI-written autobiography.
        </p>

        {gift.message && (
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-5 mb-6 text-left">
            <p className="text-sm text-gray-500 mb-1">A message from {gift.buyer_name}:</p>
            <p className="text-gray-700 italic">"{gift.message}"</p>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-8">
          Answer a few simple questions about your life and we'll create a beautifully written autobiography just for you.
        </p>

        <Button
          onClick={() => setView('questionnaire')}
          className="w-full btn-primary text-white py-6 text-base font-semibold rounded-full border-0"
          data-testid="start-gift-story-btn"
        >
          Start My Story
        </Button>
      </div>
    </div>
  );
};

export default GiftRedeem;
